import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  FlaskConical,
  KeyRound,
  ListChecks,
  Play,
  SearchCheck,
  ShieldAlert,
  XCircle
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { api, jsonBody } from '../api';
import type { AccountView, SymbolView } from '../types';
import { money } from '../components/Format';
import type { ControlView } from '../components/TradeControl';

interface TestResult {
  runId: string;
  steps: Array<{ name: string; status: 'success' | 'failed'; at: string; detail: Record<string, unknown> }>;
}

const TEST_STEPS: Array<{ icon: LucideIcon; title: string; description: string }> = [
  { icon: KeyRound, title: '账户鉴权', description: '检查 API Key 与持仓模式' },
  { icon: SearchCheck, title: '仓位预检', description: '必须空仓且没有挂单' },
  { icon: Play, title: '测试开仓', description: '提交最小额真实订单' },
  { icon: ListChecks, title: '成交核验', description: '获取订单与真实持仓' },
  { icon: CheckCircle2, title: '测试平仓', description: '关闭同等张数并核对空仓' }
];

export function TestLabPage() {
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => api<AccountView[]>('/api/accounts') });
  const symbols = useQuery({ queryKey: ['symbols'], queryFn: () => api<SymbolView[]>('/api/config/symbols') });
  const health = useQuery({ queryKey: ['health'], queryFn: () => api<{ executionEnabled: boolean }>('/api/health') });
  const control = useQuery({ queryKey: ['control'], queryFn: () => api<ControlView>('/api/control'), refetchInterval: 10_000 });
  const testAccounts = useMemo(() => (accounts.data ?? []).filter((account): account is Extract<AccountView, { purpose: 'test' | 'live' }> => account.purpose === 'test' && account.manualTestEnabled), [accounts.data]);
  const [accountId, setAccountId] = useState('');
  const [symbol, setSymbol] = useState('BTC-USDT');
  const [direction, setDirection] = useState<'long' | 'short'>('long');
  const [notional, setNotional] = useState(5);
  const [confirmation, setConfirmation] = useState('');
  const selected = testAccounts.find((account) => account.id === accountId) ?? testAccounts[0];
  const selectedId = selected?.id ?? '';

  const preflight = useMutation({
    mutationFn: () => api<Record<string, unknown>>(`/api/accounts/${selectedId}/test/preflight`, { method: 'POST', ...jsonBody({ symbol }) })
  });
  const fullCycle = useMutation({
    mutationFn: () => api<TestResult>(`/api/accounts/${selectedId}/test/full-cycle`, {
      method: 'POST',
      ...jsonBody({ symbol, direction, notionalUsdt: notional, confirmAccountName: confirmation })
    })
  });

  const ready = Boolean(
    selected &&
    selected.tradingState === 'enabled' &&
    health.data?.executionEnabled &&
    control.data && !control.data.halted &&
    confirmation === selected.name &&
    notional > 0 &&
    notional <= selected.risk.maxOrderUsdt
  );

  return (
    <div className="page-stack page-enter">
      <div className="page-heading">
        <div><h1>API 测试工具</h1><p>验证真实账户的开仓与平仓</p></div>
        <div className={`execution-badge ${health.data?.executionEnabled ? 'on' : 'off'}`}><i />{health.data?.executionEnabled ? '订单执行已开启' : '订单执行总闸关闭'}</div>
      </div>

      <div className="real-money-warning"><ShieldAlert size={21} /><div><strong>这不是模拟盘</strong><span>测试会使用真实资金成交。系统只允许 TEST 账户，并受单笔金额硬上限约束。</span></div></div>

      <section className="test-layout">
        <article className="panel test-form-panel">
          <div className="panel-heading"><h2>测试参数</h2><span className="danger-copy">真实订单</span></div>
          <div className="test-form-grid">
            <label><span>测试子账户</span><select value={selectedId} onChange={(event) => setAccountId(event.target.value)}>{testAccounts.map((account) => <option value={account.id} key={account.id}>{account.name}</option>)}</select></label>
            <label><span>交易对</span><select value={symbol} onChange={(event) => setSymbol(event.target.value)}>{(symbols.data ?? []).filter((item) => item.enabled).map((item) => <option value={item.symbol} key={item.symbol}>{item.symbol}</option>)}</select></label>
            <label className="full"><span>方向</span><div className="segmented"><button className={direction === 'long' ? 'active' : ''} onClick={() => setDirection('long')}>买入 · 做多</button><button className={direction === 'short' ? 'active' : ''} onClick={() => setDirection('short')}>卖出 · 做空</button></div></label>
            <label><span>测试金额</span><div className="amount-input"><input type="number" min="1" max={selected?.risk.maxOrderUsdt ?? 1} value={notional} onChange={(event) => setNotional(Number(event.target.value))} /><b>USDT</b></div><small>账户硬上限 {money(selected?.risk.maxOrderUsdt)} USDT</small></label>
            <label><span>二次确认</span><input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} placeholder={`输入“${selected?.name ?? '账户名称'}”`} /><small>必须输入完整账户名称</small></label>
          </div>
          <div className="test-actions"><button className="secondary-button" onClick={() => preflight.mutate()} disabled={!selectedId || preflight.isPending}>{preflight.isPending ? '正在检查' : '仅执行预检'}</button><button className="danger-button" onClick={() => fullCycle.mutate()} disabled={!ready || fullCycle.isPending}><FlaskConical size={18} />{fullCycle.isPending ? '真实测试执行中' : '执行完整真实测试'}</button></div>
          {!health.data?.executionEnabled ? <div className="inline-warning"><AlertTriangle size={16} />服务器需要设置 EXECUTION_ENABLED=true 才能提交订单。</div> : null}
          {control.data?.halted ? <div className="inline-warning"><AlertTriangle size={16} />全局紧急停单中，真实测试已禁用。</div> : null}
        </article>

        <aside className="panel receipt-panel">
          <div className="panel-heading"><h2>执行回执</h2>{fullCycle.data ? <span className="success-copy">全部通过</span> : null}</div>
          {!preflight.data && !fullCycle.data && !preflight.error && !fullCycle.error ? <div className="receipt-empty"><Circle size={42} /><strong>等待测试</strong><p>执行预检或完整测试后，所有步骤与交易所回执将在这里显示。</p></div> : null}
          {preflight.data && !fullCycle.data ? <div className="preflight-success"><CheckCircle2 /><strong>账户预检通过</strong><pre>{JSON.stringify(preflight.data, null, 2)}</pre></div> : null}
          {fullCycle.data ? <div className="receipt-timeline">{fullCycle.data.steps.map((step, index) => <div className="receipt-step" key={`${step.name}:${index}`}><CheckCircle2 /><div><strong>{step.name}</strong><small>{new Date(step.at).toLocaleTimeString('zh-CN', { hour12: false })}</small><pre>{JSON.stringify(step.detail, null, 2)}</pre></div></div>)}</div> : null}
          {(preflight.error || fullCycle.error) ? <div className="test-error"><XCircle /><strong>测试未通过</strong><p>{preflight.error?.message ?? fullCycle.error?.message}</p></div> : null}
        </aside>
      </section>
      <details className="panel test-steps">
        <summary>测试流程<span>5 个步骤</span></summary>
        <div className="test-steps-list">{TEST_STEPS.map(({ icon: Icon, title, description }, index) => {
          const complete = (fullCycle.data?.steps.length ?? 0) > index;
          return <div className={`test-step ${complete ? 'complete' : ''}`} key={title}><span>{complete ? <CheckCircle2 /> : <Icon />}</span><div><b>{index + 1}. {title}</b><small>{description}</small></div></div>;
        })}</div>
      </details>
    </div>
  );
}
